/**
 * The clearance chart: violations per station bin, on the station-volumes
 * chart's own foundation — visx axes and scales, the shared `CHART_CSS`,
 * rounded `barPath` bars, the `TooltipCard` hover card and a
 * `HiddenDataTable` for screen readers — so the two docks read as one
 * instrument.
 *
 * Two panes on one shared station axis, like the station chart's:
 *
 *  - the BAR pane, one metric at a time — violation count, max intrusion
 *    depth, or near-miss points (nested bands, drawn as a stack) — chosen by
 *    the dock's Count | Depth | Near miss control (or this chart's own, when
 *    it is used standalone). A bin with nothing to plot draws NO bar; under
 *    the baseline a 3 px status tick marks the intervals the bars cannot
 *    show (near misses under Count / Depth, violations under Near miss).
 *  - the near-miss share LADDER under it: one 14 px row per near-miss band,
 *    one cell per bar, shaded by the SHARE of the interval's scored points
 *    inside that band. It drops out when the plot is too short to give both
 *    panes a readable height (the bar pane is the one that never gives).
 *
 * There is no cumulative pane: a clearance run has no running quantity, so
 * the station dock's mass-haul pane has no analogue here.
 *
 * Interaction is the station chart's contract, to the key (ruling: click
 * walks, drag brushes): a click moves the alignment walk to that station
 * interval and clears any brush; only a DRAG sets `stationVolumeSelectionAtom`
 * (SOURCE interval indices). A one-interval brush narrowed every KPI in the
 * dock and hid the run verdict, which is why a click no longer makes one.
 *
 * Keyboard: the plot is ONE tab stop, not one per bar — a corridor-length run
 * has thousands of bars and a tab stop each would make the dock untraversable.
 * ←/→ step the walk one interval (Shift: ten, or the brush's trailing edge
 * when there is a brush), Home/End jump to the ends, Escape clears the brush.
 *
 * Sizing: the dock measures its plot box and passes `width` × `height`; the
 * chart fills exactly that, legend row included. The legend is the shared
 * 20 px `DockChartLegend` row (it never wraps); the plot still measures what
 * is LEFT under it (the fallback is only until the observer reports, and in
 * jsdom).
 *
 * There is no visible readout row: the tooltip is the readout (two stations
 * on screen at once — the walk in the row, the pointer in the tooltip — was
 * audit §A4's complaint). A visually hidden `aria-live` readout keeps the
 * walked interval announced for keyboard walking.
 */
import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import { DockChartLegend, type DockLegendItem } from '@/components/dock/DockChartLegend';
import { DockSegmented } from '@/components/dock/DockToolbar';
import { SEVERITY } from '@/components/VerdictChip';
import { internalToDisplayRaw } from '@/core/alignment';
import { useTheme } from '@/hooks/useTheme';
import {
  axisTicks,
  barHeightPx,
  barKind,
  CLEARANCE_FILL,
  CLEARANCE_LEGEND_KEYS,
  CLEARANCE_METRICS,
  DEFAULT_NEAR_MISS_BANDS_M,
  formatCountTick,
  groupClearanceBins,
  isCountMetric,
  ladderOpacity,
  ladderShare,
  metricMax,
  metricValue,
  NEAR_MISS_RUNGS,
  NEAR_MISS_TICK_PX,
  nearMissBandsOf,
  nearMissStackPx,
  superBinIndexAt,
  type ClearanceMetric,
  type ClearanceSuperBin,
} from '@/lib/clearance/chart';
import type { ClearanceJson } from '@/lib/clearance/types';
import { ruleVars } from '@/lib/clearance/useClearanceVerdict';
import { clearanceVerdict, marginalBandM } from '@/lib/clearance/verdict';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import {
  BAR_RADIUS_PX,
  barPath,
  barWidthFor,
  CHART_MARGIN,
  equationMarkers,
  maxBarsFor,
} from '@/lib/stationVolumes/chartLayout';
import { formatFixed, formatSigned, staOnly, staTag } from '@/lib/stationVolumes/format';
import type { StationVolumesJson } from '@/lib/stationVolumes/types';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Info } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHART_CSS,
  FLOATING_TOOLTIP_STYLE,
  HiddenDataTable,
  stationTickLabelProps,
  TICK_FONT,
  TooltipCard,
  tickLabelProps,
} from './StationVolumeChartParts';

export interface ClearanceChartProps {
  json: ClearanceJson;
  layerId: string;
  width: number;
  height: number;
  /** Owned by the dock so the choice survives a trip to the Table tab (L6). */
  metric: ClearanceMetric;
  onMetricChange: (next: ClearanceMetric) => void;
  /**
   * Where a click walks, when the dock owns the walk: `ClearanceDock` passes
   * its `moveCursor`, which also walks a run whose alignment is not loaded
   * (the dock's stand-in cursor). Omitted, a click moves a walk already
   * running on this run's alignment and nothing else.
   */
  onWalkTo?: (station: number) => void;
  /**
   * The INTERNAL station the cursor stands at, when the dock owns it (the walk
   * or its stand-in). Omitted, the chart reads the walk itself.
   */
  cursorStation?: number | null;
  /**
   * Render the chart's own Count | Depth | Near miss control in the legend
   * row. The dock passes `false`: it carries the control in its tab bar, and
   * two controls for one setting would be one too many. Default `true`, so the
   * chart stands on its own (and in tests).
   */
  showMetricControl?: boolean;
}

export type { ClearanceMetric };

/** The legend row (`DockChartLegend`, h-5) plus the `gap-1` under it. */
const HEADER_FALLBACK_PX = 24;
/** Floors that only keep the SVG off zero while the dock is dragged tiny. */
const MIN_PLOT_W = 120;
const MIN_PLOT_H = 40;
/**
 * The status zone under the bar baseline: 2 px of air, the 3 px status tick
 * (`NEAR_MISS_TICK_PX`), 1 px under it. Reserved whether or not any bin has a
 * tick, so the bars do not jump when the metric changes.
 */
const STATUS_GAP_PX = 2;
const STATUS_ZONE_PX = STATUS_GAP_PX + NEAR_MISS_TICK_PX + 1;
/** One ladder row, and the air between rows (spec §B4: 14 px rows). */
const LADDER_ROW_PX = 14;
const LADDER_ROW_GAP_PX = 2;
const LADDER_H = NEAR_MISS_RUNGS.length * LADDER_ROW_PX + (NEAR_MISS_RUNGS.length - 1) * LADDER_ROW_GAP_PX;
/** Air between the status zone and the ladder's first row. */
const LADDER_TOP_GAP_PX = 4;
/**
 * Below this inner plot height the ladder is dropped rather than squeezed:
 * the bar pane keeps at least ~70 px, because the bars are the headline.
 */
const LADDER_MIN_INNER_H = LADDER_H + LADDER_TOP_GAP_PX + STATUS_ZONE_PX + 70;
/**
 * Ladder cell opacity ceiling per theme. Amber on stone-900 is the most
 * saturated ink in the dark dock — at full strength the near-miss strip drew
 * more attention than the red violation bars (audit §A10), so dark caps it.
 */
const LADDER_MAX_OPACITY = { light: 1, dark: 0.45 } as const;
/**
 * The Near-miss metric's nested shades, OUTER band → inner, as `color-mix`
 * steps of the one near-miss ink (`--sv-caution`): the inner band — the
 * closest points — is the full-strength amber, each band outside it lighter.
 * Mixed, not stacked translucent rects, so the legend swatch is the exact
 * colour the bar segment is painted in.
 */
const NEAR_MISS_SHADE_PCT = [55, 78, 100] as const;
const nearMissShade = (pct: number) =>
  pct >= 100 ? CLEARANCE_FILL.nearMiss : `color-mix(in srgb, ${CLEARANCE_FILL.nearMiss} ${pct}%, transparent)`;
/**
 * The station chart's brush constants (`StationVolumeChart`): a ±12 px grab
 * zone is the 24 px A11y target, the visible grip is narrower so it does not
 * hide the bars under it.
 */
const HANDLE_GRAB_PX = 12;
const HANDLE_GRIP_H = 28;
const HANDLE_GRIP_W = 8;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Ticks are a legibility aid, not a quoted figure — 2 dp is plenty for a
 * depth. A count axis goes through `formatCountTick` (`1.5 M`, `500 k`,
 * grouped integers below 10 000) and never `Intl`'s compact notation, which
 * prints a lowercase "m" for million in en-GB (audit §A4).
 */
const tickText = (v: number, metric: ClearanceMetric): string =>
  isCountMetric(metric) ? formatCountTick(v) : formatFixed(v, 2);

const dash = (v: number | null | undefined, decimals: number): string =>
  v === null || v === undefined ? '—' : formatFixed(v, decimals);

export const ClearanceChart = ({
  json,
  layerId,
  width,
  height,
  metric,
  onMetricChange,
  onWalkTo,
  cursorStation,
  showMetricControl = true,
}: ClearanceChartProps) => {
  const { t } = useTranslation('project');
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  // Read AND write: the bar highlight follows the table's hover too.
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  /** Drives the overlay's cursor: the brush handles have no hit area of their
   *  own (the overlay does all the hit-testing), so the affordance has to be
   *  the pointer itself. */
  const [pointerCursor, setPointerCursor] = useState<'crosshair' | 'ew-resize' | 'grabbing'>('crosshair');
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  // Only ever read inside event handlers — the React Compiler forbids reading
  // a ref during render.
  const lastTipRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  /** Owns the window listeners of an in-flight drag, so unmounting mid-drag
   *  (the dock closing under the pointer) cannot leak them. */
  const dragAbortRef = useRef<AbortController | null>(null);
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true, detectBounds: true, zIndex: 9999 });
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{
    group: number;
  }>();
  const palette = useSvPalette();
  const theme = useTheme();

  // Measure the plot box under the header strip — the strip wraps on a narrow
  // dock, so its height is not a constant this file can subtract.
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || typeof r.width !== 'number' || typeof r.height !== 'number') return;
      if (r.width <= 0 || r.height <= 0) return;
      setBox((prev) => (prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A drag in flight when this unmounts would otherwise leave `pointermove`
  // on the window, writing into a dead component's atoms.
  useEffect(() => () => dragAbortRef.current?.abort(), []);

  // Review #7: the hover atom is a bare bin index with no layer key. Closing
  // the dock under a resting pointer fires no `pointerleave`, so without this
  // the next dock to open (or the pinned station bands) would highlight
  // ITS bin of the same number.
  useEffect(() => () => setHover(null), [setHover]);

  /** The plot box is both what we measure and what the tooltip is positioned
   *  against — anchoring the tooltip to the outer wrapper would land it over
   *  the header strip. */
  const setPlotRef = useCallback(
    (el: HTMLDivElement | null) => {
      plotRef.current = el;
      containerRef(el);
    },
    [containerRef],
  );

  const plotW = Math.max(MIN_PLOT_W, box?.w ?? width);
  const plotH = Math.max(MIN_PLOT_H, box?.h ?? height - HEADER_FALLBACK_PX);
  const margin = CHART_MARGIN;
  const innerW = Math.max(1, plotW - margin.left - margin.right);
  const innerH = Math.max(1, plotH - margin.top - margin.bottom);
  const showLadder = innerH >= LADDER_MIN_INNER_H;
  const barPlotH = Math.max(
    1,
    innerH - STATUS_ZONE_PX - (showLadder ? LADDER_H + LADDER_TOP_GAP_PX : 0),
  );
  const statusY = barPlotH + STATUS_GAP_PX;
  const ladderTop = barPlotH + STATUS_ZONE_PX + LADDER_TOP_GAP_PX;
  const axisY = innerH;

  // Aggregate before drawing: one mark per source bin is fine at 250 bins and
  // ruinous at 4 000 (M3).
  const { bins, groupSize } = useMemo(() => groupClearanceBins(json.bins, maxBarsFor(innerW)), [json.bins, innerW]);
  const barW = barWidthFor(innerW, Math.max(bins.length, 1));
  const maxValue = useMemo(() => metricMax(bins, metric), [bins, metric]);
  const ticks = useMemo(() => axisTicks(maxValue, 4, isCountMetric(metric)), [maxValue, metric]);
  // Read off the SOURCE bins, not the grouped ones: a super-bin that swallows
  // one unscored bin among ten measured ones still has points, and the reader
  // is entitled to know that some ground under this axis was never swept.
  const anyUnscored = useMemo(() => json.bins.some((b) => b.pointCount === 0), [json.bins]);
  const bands = nearMissBandsOf(json.params);
  const bandLabel = (k: number) => formatFixed(bands[k] ?? DEFAULT_NEAR_MISS_BANDS_M[k], 2);
  /** The amber rung — the band `k2Verdict` and the MARGINAL verdict turn on. */
  const marginalBand = formatFixed(marginalBandM(json.params.nearMissBandsM), 2);

  // ------------------------------------------------------------ scales
  /**
   * INTERNAL stations, exactly as the station chart's x-axis: ticks land on
   * round internal stations and are labelled through the run's equations, so
   * the two docks' axes read the same at the same chainage. The domain is the
   * bins' own extent — the stretch that was swept.
   */
  const lo = json.bins.length ? json.bins[0].staStartInternalM : 0;
  const hi = json.bins.length ? json.bins[json.bins.length - 1].staEndInternalM : 1;
  const xScale = useMemo(
    () => scaleLinear<number>({ domain: [lo, hi > lo ? hi : lo + 1], range: [0, innerW] }),
    [lo, hi, innerW],
  );
  const yScale = useMemo(
    () => scaleLinear<number>({ domain: [0, maxValue], range: [barPlotH, 0] }),
    [maxValue, barPlotH],
  );
  const displayOf = useCallback(
    (internal: number) => internalToDisplayRaw(internal, json.alignment.staStart, json.alignment.equations),
    [json.alignment.staStart, json.alignment.equations],
  );
  /**
   * The station chart's equation markers. `equationMarkers` reads only the
   * alignment's equations and the analysed range, which a clearance run
   * carries under the same names — the cast is to that shared shape, not a
   * claim that this is a station-volumes run.
   */
  const equations = useMemo(
    () =>
      equationMarkers({
        alignment: json.alignment,
        params: { stationFromInternalM: lo, stationToInternalM: hi },
      } as unknown as StationVolumesJson),
    [json.alignment, lo, hi],
  );
  const numXTicks = Math.max(2, Math.floor(innerW / 90));
  const staTick = (v: number) => staOnly(displayOf(v), 0);

  // ------------------------------------------------------------ live state
  const nSrc = json.bins.length;
  /**
   * The brush, only while it covers at least one of THIS run's intervals —
   * the dock's `selEff` rule (review #8). A brush left in the shared atom by a
   * longer run used to be clamped onto the last bar and drawn there, dimming
   * every bar, while the dock header said "Whole run" and offered no Clear.
   * Order-independent: a leftward drag writes `fromBin > toBin`.
   */
  const rawSel = selection && selection.layerId === layerId ? selection : null;
  const rawLo = rawSel ? Math.min(rawSel.fromBin, rawSel.toBin) : 0;
  const rawHi = rawSel ? Math.max(rawSel.fromBin, rawSel.toBin) : -1;
  const sel = rawSel && json.bins.some((b) => b.i >= rawLo && b.i <= rawHi) ? rawSel : null;
  const centreX = (b: ClearanceSuperBin) => xScale(binCentreStation(b));
  // The brushed span off the SOURCE bins, as the station chart draws it.
  const selLo = sel ? clamp(rawLo, 0, nSrc - 1) : 0;
  const selHi = sel ? clamp(rawHi, 0, nSrc - 1) : 0;
  const selX1 = sel ? xScale(json.bins[selLo].staStartInternalM) : 0;
  const selX2 = sel ? xScale(json.bins[selHi].staEndInternalM) : 0;
  const inSelOf = (b: ClearanceSuperBin) => !sel || (b.toBin >= selLo && b.fromBin <= selHi);

  /** The walked station, only while the walk is on THIS run's alignment. */
  const walkStation =
    cursorStation !== undefined ? cursorStation : walk?.key === json.alignment.key ? walk.station : null;
  const walkInRange = walkStation != null && walkStation >= lo && walkStation <= hi;
  /** The SOURCE interval the walk is in — what ←/→ step from. */
  const cursorBin = walkStation == null || nSrc === 0 ? null : superBinIndexAt(json.bins, walkStation);
  const walkedSrc = cursorBin === null || !walkInRange ? null : json.bins[cursorBin];

  // The hover highlight (pointer or table) is a crosshair and the tooltip; the
  // hidden readout is NOT a copy of the tooltip — it names the walked
  // interval, else sums the brushed range, so it says where the reader IS.
  const hovered = hoverBin === null ? null : (bins.find((b) => hoverBin >= b.fromBin && hoverBin <= b.toBin) ?? null);
  const walkedBar = walkedSrc ? (bins.find((b) => walkedSrc.i >= b.fromBin && walkedSrc.i <= b.toBin) ?? null) : null;
  const readoutBin = useMemo<ClearanceSuperBin | null>(() => {
    // One-member `groupClearanceBins` calls: the same sum / max reduction the
    // bars use, so the readout and a grouped bar can never disagree.
    if (walkedSrc) return groupClearanceBins([walkedSrc], 1).bins[0];
    if (sel) return groupClearanceBins(json.bins.slice(selLo, selHi + 1), 1).bins[0];
    return null;
  }, [walkedSrc, sel, json.bins, selLo, selHi]);

  // ------------------------------------------------------------ interaction
  /** SOURCE interval under a plot-local x. */
  const binAtLocalX = (x: number) => superBinIndexAt(json.bins, xScale.invert(x));
  /** Drawn bar under a plot-local x — what the tooltip describes. */
  const groupAtLocalX = (x: number) => superBinIndexAt(bins, xScale.invert(x));

  /**
   * Move the walk to source interval `i`'s centre — the station-volumes
   * chart's `walkToBin`. `follow` is never switched on: a click is a request
   * to look at a station, not to have the camera taken over (the walk
   * toolbar's ⦿ is where the user asks for that). A no-op unless a walk is
   * already running on THIS run's alignment, so a click can neither start a
   * walk behind the gate nor yank one following a different alignment.
   */
  const walkToBin = (i: number) => {
    const b = json.bins[i];
    if (!b) return;
    const station = binCentreStation(b);
    if (onWalkTo) onWalkTo(station);
    else setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
  };

  /** True inside either brush edge's ±12 px grab zone. */
  const nearHandle = (x: number) =>
    !!sel && (Math.abs(x - selX1) <= HANDLE_GRAB_PX || Math.abs(x - selX2) <= HANDLE_GRAB_PX);

  const onOverlayMove = (e: ReactPointerEvent<SVGRectElement>) => {
    const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
    // The cursor tracks the pointer, not the bar: you can cross into a grab
    // zone without changing bar.
    if (!draggingRef.current) setPointerCursor(nearHandle(x) ? 'ew-resize' : 'crosshair');
    const g = groupAtLocalX(x);
    if (g < 0) return;
    const src = binAtLocalX(x);
    if (src === lastTipRef.current) return;
    lastTipRef.current = src;
    setHover(src);
    const b = bins[g];
    showTooltip({ tooltipData: { group: g }, tooltipLeft: margin.left + centreX(b), tooltipTop: margin.top + 4 });
  };

  const onOverlayLeave = () => {
    lastTipRef.current = null;
    if (!draggingRef.current) setPointerCursor('crosshair');
    setHover(null);
    hideTooltip();
  };

  /**
   * Brush + click-to-walk, ported from `StationVolumeChart.onOverlayDown`.
   * `pointermove`/`pointerup` go on the window so a drag that leaves the plot
   * keeps tracking instead of freezing halfway.
   */
  const onOverlayDown = (e: ReactPointerEvent<SVGRectElement>) => {
    if (e.button !== 0 || nSrc === 0) return;
    const originLeft = e.currentTarget.getBoundingClientRect().left;
    const downX = e.clientX - originLeft;
    const downBin = binAtLocalX(downX);

    // Grabbing an existing brush edge re-drags that edge; the opposite edge
    // becomes the anchor.
    let anchor = downBin;
    let handleDrag = false;
    if (sel) {
      if (Math.abs(downX - selX1) <= HANDLE_GRAB_PX) {
        anchor = selHi;
        handleDrag = true;
      } else if (Math.abs(downX - selX2) <= HANDLE_GRAB_PX) {
        anchor = selLo;
        handleDrag = true;
      }
    }

    let moved = handleDrag;
    draggingRef.current = true;
    setPointerCursor('grabbing');

    dragAbortRef.current?.abort();
    const ac = new AbortController();
    dragAbortRef.current = ac;

    const move = (ev: globalThis.PointerEvent) => {
      const i = binAtLocalX(ev.clientX - originLeft);
      if (i !== downBin) moved = true;
      // Stored in order, so every reader of the atom (the dock's `selEff`
      // among them) sees `fromBin <= toBin` whichever way the drag went.
      if (moved) setSelection({ layerId, fromBin: Math.min(anchor, i), toBin: Math.max(anchor, i) });
    };
    const up = (ev: globalThis.PointerEvent) => {
      ac.abort();
      if (dragAbortRef.current === ac) dragAbortRef.current = null;
      draggingRef.current = false;
      setPointerCursor(nearHandle(ev.clientX - originLeft) ? 'ew-resize' : 'crosshair');
      if (moved) return;
      // A click, not a drag: clear the brush and walk to that interval.
      setSelection(null);
      walkToBin(binAtLocalX(ev.clientX - originLeft));
    };
    window.addEventListener('pointermove', move, { signal: ac.signal });
    window.addEventListener('pointerup', up, { signal: ac.signal });
    window.addEventListener('pointercancel', up, { signal: ac.signal });
  };

  /** `StationVolumeChart.onKeyDown`, key for key — see the file header. */
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (nSrc === 0) return;
    if (e.key === 'Escape') {
      if (!sel) return; // nothing of ours to clear — let the dock close
      e.preventDefault();
      e.stopPropagation();
      setSelection(null);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      walkToBin(e.key === 'Home' ? 0 : nSrc - 1);
      return;
    }
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (dir === 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey && sel) {
      const edge = clamp(sel.toBin + dir, 0, nSrc - 1);
      setSelection({ layerId, fromBin: Math.min(sel.fromBin, edge), toBin: Math.max(sel.fromBin, edge) });
      return;
    }
    // No walk yet: → lands on the FIRST interval and ← on the last (review
    // #16 — `(cursorBin ?? 0) + 1` skipped interval 0 entirely).
    if (cursorBin === null) {
      walkToBin(dir > 0 ? 0 : nSrc - 1);
      return;
    }
    walkToBin(clamp(cursorBin + dir * (e.shiftKey ? 10 : 1), 0, nSrc - 1));
  };

  // ------------------------------------------------------------ copy
  const rangeOf = (b: ClearanceSuperBin) => `${staTag(b.staStartDisplayM, 2)} – ${staOnly(b.staEndDisplayM, 2)}`;
  /** The readout sentence. Plural on the violation count; the near-miss
   *  figure is the OUTERMOST band's, the one that turns a bar amber. */
  const barLabel = (b: ClearanceSuperBin) => {
    const vars = {
      count: b.violationCount,
      formatted: b.violationCount.toLocaleString(),
      range: rangeOf(b),
      near: b.nearMiss.within0_35M.toLocaleString(),
      band: marginalBand,
    };
    // No intrusion, no depth clause: "max intrusion — m" is a unit on nothing.
    return b.maxIntrusionM == null
      ? t('clearance.chart.barLabelClear', vars)
      : t('clearance.chart.barLabel', { ...vars, depth: formatFixed(b.maxIntrusionM, 3) });
  };
  const verdictOf = (b: ClearanceSuperBin) =>
    clearanceVerdict({ violationCount: b.violationCount, scoredCount: b.pointCount, nearMiss: b.nearMiss });
  const verdictCell = (b: ClearanceSuperBin) => {
    const v = verdictOf(b);
    return `${SEVERITY[v].glyph} ${t(`expectation.verdict.${v}`)}`;
  };
  const nearMissHeader = (k: number) => t('clearance.table.nearMissBand', { value: bandLabel(k) });

  const tipBin = tooltipData ? bins[tooltipData.group] : null;
  const tipRows = tipBin
    ? (() => {
        const rows: { label: string; value: string }[] = [
          { label: t('clearance.chart.verdict'), value: verdictCell(tipBin) },
          { label: t('clearance.table.violations'), value: tipBin.violationCount.toLocaleString() },
          { label: t('clearance.table.maxIntrusion'), value: dash(tipBin.maxIntrusionM, 3) },
          {
            label: t('clearance.table.worstOffset'),
            value: tipBin.worstOffsetM == null ? '—' : formatSigned(tipBin.worstOffsetM, 2),
          },
          { label: t('clearance.table.worstHeight'), value: dash(tipBin.worstHeightM, 2) },
          ...NEAR_MISS_RUNGS.map((k, idx) => {
            const share = ladderShare(tipBin.nearMiss[k], tipBin.pointCount);
            return {
              label: t('clearance.chart.nearMissBand', { band: bandLabel(idx) }),
              // Count AND share: the ladder is shaded by the share, and the
              // count alone mostly measures point density.
              value:
                share === null
                  ? tipBin.nearMiss[k].toLocaleString()
                  : t('clearance.chart.countShare', {
                      value: tipBin.nearMiss[k].toLocaleString(),
                      pct: formatFixed(share * 100, 1),
                    }),
            };
          }),
          { label: t('clearance.table.points'), value: tipBin.pointCount.toLocaleString() },
        ];
        // An equation inside the hovered bar is the one thing that makes the
        // display stations either side of it discontinuous.
        const last = tipBin.toBin === json.bins.length - 1;
        for (const e of equations) {
          const inBin =
            e.station >= tipBin.staStartInternalM &&
            (e.station < tipBin.staEndInternalM || (last && e.station <= tipBin.staEndInternalM));
          if (inBin) {
            rows.push({
              label: t('stationVolumes.chart.equationRow'),
              value: t('stationVolumes.chart.equationTag', { from: staOnly(e.from), to: staOnly(e.to) }),
            });
          }
        }
        return rows;
      })()
    : [];

  const tableHeaders = [
    t('clearance.table.station'),
    t('clearance.chart.verdict'),
    t('clearance.table.violations'),
    t('clearance.table.maxIntrusion'),
    t('clearance.table.worstOffset'),
    t('clearance.table.worstHeight'),
    ...NEAR_MISS_RUNGS.map((_, k) => nearMissHeader(k)),
  ];
  const tableRows = bins.map((b) => ({
    key: b.fromBin,
    cells: [
      `${staOnly(b.staStartDisplayM)} – ${staOnly(b.staEndDisplayM)}`,
      verdictCell(b),
      b.violationCount.toLocaleString(),
      dash(b.maxIntrusionM, 3),
      b.worstOffsetM == null ? '—' : formatSigned(b.worstOffsetM, 2),
      dash(b.worstHeightM, 2),
      ...NEAR_MISS_RUNGS.map((k) => b.nearMiss[k].toLocaleString()),
    ],
  }));

  /**
   * The run's worst intrusion, marked on the axis so "where is it?" has an
   * answer on the Chart tab without hunting the tallest red bar (which, in the
   * count metric, is the most-violated interval — not the deepest).
   */
  const worstInternal = json.summary.violationCount > 0 ? json.summary.worstStationInternalM : null;
  const worstX = worstInternal != null && worstInternal >= lo && worstInternal <= hi ? xScale(worstInternal) : null;
  const worstTitle =
    worstX === null || worstInternal == null
      ? ''
      : t('clearance.chart.worstMarker', {
          station: staTag(json.summary.worstStationDisplayM ?? displayOf(worstInternal), 2),
          depth: dash(json.summary.maxIntrusionM, 3),
        });

  const metricShort: Record<ClearanceMetric, string> = {
    count: t('clearance.chart.metricCountShort'),
    depth: t('clearance.chart.metricDepthShort'),
    nearMiss: t('clearance.chart.metricNearMissShort'),
  };
  const metricHelp: Record<ClearanceMetric, string> = {
    count: t('clearance.chart.metricCount'),
    depth: t('clearance.chart.metricDepth'),
    nearMiss: t('clearance.chart.metricNearMissHelp'),
  };
  /** The y quantity, named in the legend row's right slot (no SVG caption
   *  row to collide with the top tick any more). */
  const axisTitle =
    metric === 'count'
      ? t('clearance.chart.metricCount')
      : metric === 'depth'
        ? t('clearance.table.maxIntrusion')
        : t('clearance.chart.metricNearMiss');

  /**
   * A legend swatch as a styled span, not `DockSwatch`'s inline SVG: the
   * same 10 px geometry, but `background` takes the `var(--sv-*, fallback)`
   * string as is, and the dock's tests read it back off the style to prove
   * the key and the bars share one map.
   */
  const swatch = (color: string, shape: 'box' | 'tick', testId = 'clearance-chart-legend-swatch') => (
    <span aria-hidden className='flex size-3 shrink-0 items-end justify-center'>
      <span
        data-testid={testId}
        className={shape === 'box' ? 'mb-px inline-block size-2.5 rounded-[2px]' : 'mb-px inline-block h-[3px] w-2.5'}
        style={{ background: color }}
      />
    </span>
  );
  const legendItems: DockLegendItem[] = [];
  if (metric === 'nearMiss') {
    // Inner band first — the closest points, the full-strength amber.
    for (let k = 0; k < NEAR_MISS_RUNGS.length; k++) {
      legendItems.push({
        key: `nm-${k}`,
        label: k === 0 ? t('clearance.chart.nearMissBand', { band: bandLabel(k) }) : nearMissHeader(k),
        swatch: swatch(nearMissShade(NEAR_MISS_SHADE_PCT[NEAR_MISS_RUNGS.length - 1 - k]), 'box'),
      });
    }
    // Keyed only when a red tick is actually drawn.
    if (bins.some((b) => barKind(b) === 'violation')) {
      legendItems.push({
        key: 'violation',
        label: t(CLEARANCE_LEGEND_KEYS.violation),
        title: t('clearance.chart.violationStatus'),
        swatch: swatch(CLEARANCE_FILL.violation, 'tick'),
      });
    }
  } else {
    legendItems.push({
      key: 'violation',
      label: t(CLEARANCE_LEGEND_KEYS.violation),
      swatch: swatch(CLEARANCE_FILL.violation, 'box'),
    });
    // Amber is the MARGINAL interval verdict; its tooltip quotes the
    // thresholds. A TICK, because that is the mark under this metric.
    legendItems.push({
      key: 'nearMiss',
      label: t(CLEARANCE_LEGEND_KEYS.nearMiss),
      title: t('clearance.chart.nearMissStatus', ruleVars(marginalBand)),
      swatch: swatch(CLEARANCE_FILL.nearMiss, 'tick'),
    });
  }
  // The one interval state no mark can draw: a bin the sweep never scored is
  // not "clear", it is unmeasured — an empty slot that reads like a zero. The
  // envelope paints such a span flat pale, so the key is a flat pale square;
  // the outline keeps a near-white swatch visible on a near-white panel.
  if (anyUnscored) {
    legendItems.push({
      key: 'empty',
      testId: 'clearance-chart-legend-empty',
      label: t('clearance.bin.noPoints'),
      swatch: (
        <span aria-hidden className='flex size-3 shrink-0 items-center justify-center'>
          <span
            className='inline-block size-2.5 rounded-[2px] outline outline-slate-400/50 dark:outline-stone-500/50'
            style={{ background: palette.zero }}
          />
        </span>
      ),
    });
  }
  if (showLadder) {
    legendItems.push({
      key: 'ladder',
      testId: 'clearance-ladder-caption',
      label: t('clearance.chart.ladderAxis'),
      title: t('clearance.chart.ladderHelp'),
      swatch: (
        <span aria-hidden className='flex size-3 shrink-0 items-center'>
          <span
            className='inline-block h-2.5 w-3 border-b border-slate-400 dark:border-stone-500'
            style={{ background: `color-mix(in srgb, ${CLEARANCE_FILL.nearMiss} 35%, transparent)` }}
          />
        </span>
      ),
    });
  }

  return (
    <div
      className='flex min-w-0 flex-col gap-1 overflow-hidden text-slate-700 dark:text-stone-300'
      style={{ width, height }}
    >
      <style>{CHART_CSS}</style>

      {/* The shared one-row legend (`DockChartLegend`). It sits OUTSIDE the
          chart region on purpose: the chart is one tab stop, and a control
          nested inside it would be a second one no keyboard user could reach.
          The right slot carries what used to be the caption row and the
          readout row: the y quantity, the grouping chip, the metric control
          when the dock does not carry it, and the interaction hint as ⓘ. */}
      <DockChartLegend
        testId='clearance-chart-legend'
        ariaLabel={t('clearance.chart.legendTitle')}
        items={legendItems}
        right={
          <>
            {groupSize > 1 && (
              <span className='sv-chip sv-num' data-testid='clearance-chart-grouped'>
                {t('clearance.chart.grouped', { size: groupSize })}
              </span>
            )}
            <span data-testid='clearance-chart-metric' className='font-medium text-slate-700 dark:text-stone-300'>
              {axisTitle}
            </span>
            {showMetricControl && (
              <DockSegmented<ClearanceMetric>
                testId='clearance-metric-toggle'
                ariaLabel={t('clearance.chart.metricGroup')}
                className='h-5'
                value={metric}
                onChange={onMetricChange}
                options={CLEARANCE_METRICS.map((m) => ({
                  value: m,
                  label: metricShort[m],
                  title: metricHelp[m],
                  testId: `clearance-metric-${m}`,
                }))}
              />
            )}
            <span
              data-testid='clearance-chart-hint'
              role='img'
              aria-label={t('clearance.chart.readoutHint')}
              title={t('clearance.chart.readoutHint')}
              className='flex cursor-help items-center opacity-70'
            >
              <Info className='size-3.5' aria-hidden />
            </span>
          </>
        }
      />

      {/* ---- Chart region: the single tab stop, arrow-driven ---- */}
      <div
        data-testid='clearance-chart'
        className='sv-chart flex min-h-0 w-full flex-1 flex-col outline-none'
        role='application'
        tabIndex={0}
        aria-label={t('clearance.chart.ariaCursor', {
          station: walkStation == null ? t('stationVolumes.chart.noCursor') : staTag(displayOf(walkStation), 2),
        })}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setHover(null);
          hideTooltip();
        }}
      >
        <div ref={setPlotRef} className='relative min-h-0 w-full flex-1 overflow-hidden'>
          <svg width={plotW} height={plotH} role='img' aria-label={t('clearance.chart.alt')} className='block'>
            <Group left={margin.left} top={margin.top}>
              {/* Gridlines first, under both panes, as in the station chart. */}
              {xScale.ticks(numXTicks).map((tick) => (
                <line
                  key={`gx-${tick}`}
                  x1={xScale(tick)}
                  x2={xScale(tick)}
                  y1={0}
                  y2={axisY}
                  stroke='var(--sv-grid)'
                  pointerEvents='none'
                />
              ))}
              {ticks.map((v) => (
                <line
                  key={`gy-${v}`}
                  x1={0}
                  x2={innerW}
                  y1={yScale(v)}
                  y2={yScale(v)}
                  stroke='var(--sv-grid)'
                  pointerEvents='none'
                />
              ))}
              <AxisLeft
                scale={yScale}
                tickValues={ticks}
                stroke='currentColor'
                tickStroke='currentColor'
                tickFormat={(v) => tickText(Number(v), metric)}
                tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
              />

              {/* The brush behind the bars, with the station chart's grips —
                  a brush is a range now (drag), so its edges are controls. */}
              {sel && (
                <g data-testid='clearance-chart-brush' pointerEvents='none'>
                  <rect
                    data-testid='clearance-chart-selection'
                    x={Math.min(selX1, selX2)}
                    width={Math.max(2, Math.abs(selX2 - selX1))}
                    y={0}
                    height={axisY}
                    fill='currentColor'
                    fillOpacity={0.07}
                  />
                  {[selX1, selX2].map((x, i) => {
                    const gripY = Math.max(0, (axisY - HANDLE_GRIP_H) / 2);
                    return (
                      <g key={`h-${i}`}>
                        <line
                          className='sv-handle'
                          x1={x}
                          x2={x}
                          y1={0}
                          y2={axisY}
                          stroke='var(--sv-haul)'
                          strokeOpacity={0.7}
                          strokeWidth={2}
                        />
                        <rect
                          className='sv-handle'
                          data-testid='clearance-chart-brush-handle'
                          x={x - HANDLE_GRIP_W / 2}
                          y={gripY}
                          width={HANDLE_GRIP_W}
                          height={Math.min(HANDLE_GRIP_H, Math.max(1, axisY))}
                          rx={HANDLE_GRIP_W / 2}
                          fill='var(--sv-haul)'
                          stroke='var(--sv-surface)'
                          strokeWidth={2}
                        />
                        <line
                          x1={x - 1.5}
                          x2={x - 1.5}
                          y1={gripY + 9}
                          y2={gripY + HANDLE_GRIP_H - 9}
                          stroke='var(--sv-surface)'
                        />
                        <line
                          x1={x + 1.5}
                          x2={x + 1.5}
                          y1={gripY + 9}
                          y2={gripY + HANDLE_GRIP_H - 9}
                          stroke='var(--sv-surface)'
                        />
                      </g>
                    );
                  })}
                </g>
              )}

              {bins.map((b) => {
                // Centred on its station span, so a bar and the axis tick under
                // it describe the same place (M2).
                const cx = centreX(b);
                const x = cx - barW / 2;
                const kind = barKind(b);
                const isHover = hovered?.i === b.i || walkedBar?.i === b.i;
                const opacity = inSelOf(b) ? (isHover ? 1 : 0.85) : 0.3;
                if (metric === 'nearMiss') {
                  // The nested stack: heights from the baseline, OUTER band
                  // first; each inner band is painted as its own segment from
                  // the baseline up, over the band outside it.
                  const stack = nearMissStackPx(b, maxValue, barPlotH).map((v) => Math.min(barPlotH, v));
                  const total = stack[0];
                  if (total <= 0) return null;
                  return (
                    <g
                      key={b.i}
                      data-testid={`clearance-bar-${b.fromBin}`}
                      data-kind={kind}
                      data-value={metricValue(b, metric)}
                      data-height={total}
                      data-cx={cx}
                      opacity={opacity}
                      pointerEvents='none'
                    >
                      {stack.map((h, k) =>
                        h > 0 ? (
                          <path
                            key={k}
                            data-testid={`clearance-stack-${NEAR_MISS_RUNGS.length - 1 - k}-${b.fromBin}`}
                            data-height={h}
                            d={barPath(x, barPlotH - h, barW, h, k === 0 ? BAR_RADIUS_PX : 0, 'top')}
                            fill={nearMissShade(NEAR_MISS_SHADE_PCT[k])}
                          />
                        ) : null,
                      )}
                    </g>
                  );
                }
                const h = Math.min(barPlotH, barHeightPx(b, metric, maxValue, barPlotH));
                // Zero plotted: NO bar (audit §A4). The status tick below
                // carries the interval's near-miss state instead.
                if (h <= 0) return null;
                return (
                  <path
                    key={b.i}
                    data-testid={`clearance-bar-${b.fromBin}`}
                    data-kind={kind}
                    data-value={metricValue(b, metric)}
                    data-height={h}
                    data-cx={cx}
                    d={barPath(x, barPlotH - h, barW, h, BAR_RADIUS_PX, 'top')}
                    fill={CLEARANCE_FILL[kind]}
                    opacity={opacity}
                    pointerEvents='none'
                  />
                );
              })}
              {/* The status ticks, UNDER the baseline: the interval state the
                  bars of this metric cannot draw — amber near misses under
                  Count / Depth (they plot zero there), red violations under
                  Near miss (the stack counts only points that did NOT
                  intrude). One 3 px tick per bar, at the bar's own width. */}
              <g data-testid='clearance-status-ticks' pointerEvents='none'>
                {bins.map((b) => {
                  const kind = barKind(b);
                  const shown = metric === 'nearMiss' ? kind === 'violation' : kind === 'nearMiss';
                  if (!shown) return null;
                  return (
                    <rect
                      key={b.i}
                      data-testid={`clearance-status-${b.fromBin}`}
                      data-kind={kind}
                      x={centreX(b) - barW / 2}
                      y={statusY}
                      width={barW}
                      height={NEAR_MISS_TICK_PX}
                      fill={CLEARANCE_FILL[kind]}
                      opacity={inSelOf(b) ? 1 : 0.3}
                    />
                  );
                })}
              </g>
              {/* The zero baseline, in ink — the station chart's datum line. */}
              <line
                className='sv-zero-line'
                x1={0}
                x2={innerW}
                y1={barPlotH}
                y2={barPlotH}
                stroke='currentColor'
                strokeOpacity={0.35}
                strokeWidth={1.5}
                pointerEvents='none'
              />

              {/* The near-miss share ladder. Its caption is a legend item now
                  (`clearance-ladder-caption`): in the gutter it overprinted
                  the bar pane's "0" tick. */}
              {showLadder && (
                <Group top={ladderTop} data-testid='clearance-ladder'>
                  {NEAR_MISS_RUNGS.map((k, row) => {
                    const y = row * (LADDER_ROW_PX + LADDER_ROW_GAP_PX);
                    return (
                      <g key={k}>
                        {/* The empty track, so a rung with no near misses reads
                            as "measured, none" rather than as missing — and
                            a baseline under it, so the row reads as a row
                            even when every cell is empty. */}
                        <rect x={0} y={y} width={innerW} height={LADDER_ROW_PX} fill='var(--sv-grid)' />
                        <line
                          data-testid='clearance-ladder-baseline'
                          x1={0}
                          x2={innerW}
                          y1={y + LADDER_ROW_PX - 0.5}
                          y2={y + LADDER_ROW_PX - 0.5}
                          stroke='currentColor'
                          strokeOpacity={0.25}
                        />
                        <text
                          x={-4}
                          y={y + LADDER_ROW_PX / 2}
                          dy='0.32em'
                          textAnchor='end'
                          fontSize={TICK_FONT}
                          fill='currentColor'
                          className='sv-num'
                          opacity={0.7}
                        >
                          {nearMissHeader(row)}
                        </text>
                        {bins.map((b) => {
                          const n = b.nearMiss[k];
                          const share = ladderShare(n, b.pointCount);
                          if (n <= 0 || share === null) return null;
                          return (
                            <rect
                              key={b.i}
                              data-testid={`clearance-ladder-${row}-${b.fromBin}`}
                              data-count={n}
                              data-share={share}
                              x={xScale(b.staStartInternalM)}
                              y={y}
                              width={Math.max(1, xScale(b.staEndInternalM) - xScale(b.staStartInternalM))}
                              height={LADDER_ROW_PX - 1}
                              fill={CLEARANCE_FILL.nearMiss}
                              fillOpacity={ladderOpacity(share, LADDER_MAX_OPACITY[theme]) * (inSelOf(b) ? 1 : 0.4)}
                              pointerEvents='none'
                            />
                          );
                        })}
                      </g>
                    );
                  })}
                </Group>
              )}

              {/* ------------------------------------------------ shared station axis */}
              <AxisBottom
                scale={xScale}
                top={axisY}
                numTicks={numXTicks}
                stroke='currentColor'
                tickStroke='currentColor'
                // 0 dp on ticks, like the station chart's `0+050`: a tick is a
                // place to read the axis by, not a quoted station.
                tickFormat={(v) => staTick(Number(v))}
                tickLabelProps={stationTickLabelProps(xScale, staTick, innerW + margin.right, { className: 'sv-sta' })}
              />

              {/* Station equations: the x-axis is in display stations, so the
                  jump has to be marked or the axis silently lies. */}
              {equations.map((e) => (
                <g key={`eq-${e.station}`} data-testid='clearance-chart-equation' pointerEvents='none'>
                  <line
                    x1={xScale(e.station)}
                    x2={xScale(e.station)}
                    y1={0}
                    y2={axisY}
                    stroke='currentColor'
                    strokeOpacity={0.45}
                    strokeDasharray='3,3'
                  />
                  <polygon
                    points={`${xScale(e.station)},${axisY - 5} ${xScale(e.station) - 4},${axisY + 2} ${xScale(e.station) + 4},${axisY + 2}`}
                    fill='currentColor'
                  />
                </g>
              ))}

              {/* The hover crosshair, across both panes — it follows the
                  table's hover too, through the shared atom. */}
              {hovered && (
                <line
                  className='sv-hover-cursor'
                  data-testid='clearance-chart-hover'
                  x1={centreX(hovered)}
                  x2={centreX(hovered)}
                  y1={0}
                  y2={axisY}
                  stroke='currentColor'
                  strokeOpacity={0.4}
                  strokeDasharray='2,2'
                />
              )}
              {/* The run's deepest intrusion: a caret on the axis and a faint
                  rule, in the violation ink, so it reads as data, not as the
                  walk (amber) or an equation (ink). */}
              {worstX !== null && (
                <g data-testid='clearance-chart-worst' pointerEvents='none'>
                  <line
                    x1={worstX}
                    x2={worstX}
                    y1={0}
                    y2={axisY}
                    stroke='var(--sv-cut)'
                    strokeOpacity={0.45}
                    strokeDasharray='2,3'
                  />
                  <polygon
                    points={`${worstX},${axisY - 1} ${worstX - 4},${axisY - 7} ${worstX + 4},${axisY - 7}`}
                    fill='var(--sv-cut)'
                  />
                  <title>{worstTitle}</title>
                </g>
              )}
              {/* The walked station — the station chart's walk cursor, so the
                  keyboard and a click both have a visible place they moved. */}
              {walkInRange && walkStation != null && (
                <g
                  className='sv-walk-cursor'
                  data-testid='clearance-chart-cursor'
                  style={{ transform: `translateX(${xScale(walkStation)}px)` }}
                >
                  <line x1={0} x2={0} y1={0} y2={axisY} stroke='var(--sv-walk)' strokeWidth={1.5} />
                  <polygon points={`0,${axisY} -4,${axisY + 6} 4,${axisY + 6}`} fill='var(--sv-walk)' />
                </g>
              )}

              {/* Interaction overlay: hover, click-to-walk, drag-to-brush,
                  across both panes, so even a 1 px bar has a full-height hit
                  target. */}
              <rect
                data-testid='clearance-chart-overlay'
                x={0}
                y={0}
                width={innerW}
                height={Math.max(1, axisY)}
                fill='transparent'
                // `touch-action: none`: on touch the browser would claim the
                // drag (pointercancel) and the walk / brush would stop.
                style={{ cursor: pointerCursor, touchAction: 'none' }}
                onPointerDown={onOverlayDown}
                onPointerMove={onOverlayMove}
                onPointerLeave={onOverlayLeave}
              />
            </Group>
          </svg>

          {tooltipOpen && tipBin && (
            <TooltipInPortal
              top={tooltipTop}
              left={tooltipLeft}
              style={FLOATING_TOOLTIP_STYLE}
              className='pointer-events-none !rounded-md !bg-slate-800 !px-2 !py-1.5 !text-xs !text-white !shadow-lg'
            >
              <TooltipCard station={rangeOf(tipBin)} rows={tipRows} />
            </TooltipInPortal>
          )}
        </div>
      </div>

      {/* The walked interval (else the brushed range), for assistive tech
          only: sighted readers have the tooltip and the walk cursor, and a
          visible row that named a different station than the tooltip was
          audit §A4's complaint. Outside the application region so it is
          announced, not swallowed. */}
      <span data-testid='clearance-chart-readout' aria-live='polite' className='sr-only'>
        {readoutBin ? barLabel(readoutBin) : t('clearance.chart.readoutHint')}
      </span>

      {/* OUTSIDE the `role="application"` region, deliberately — see the
          station chart: inside it, the rows are unreachable by table
          navigation. */}
      <HiddenDataTable
        caption={
          groupSize > 1 ? t('clearance.chart.srCaptionGrouped', { k: groupSize }) : t('clearance.chart.srCaption')
        }
        headers={tableHeaders}
        rows={tableRows}
        currentRow={hovered?.fromBin ?? walkedBar?.fromBin ?? null}
      />
    </div>
  );
};
